import React, { useEffect, useRef, useState } from "react";
import { Spinner, Pagination } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import { getUserAnswersByAttempt } from "../../../services/QuestionService";
import { useParams } from "react-router-dom";

const ViewUserQuizAttempt = () => {
    const { id } = useParams(); // attempt_id
    const hasFetched = useRef(false);

    const [loading, setLoading] = useState(false);
    const [answers, setAnswers] = useState([]);
    const [attemptInfo, setAttemptInfo] = useState(null);

    const [page, setPage] = useState(1);
    const limit = 5;
    const [pagination, setPagination] = useState({
        total: 0,
        totalPages: 0,
    });

    const fetchUserAnswers = async (pageNo = 1) => {
        try {
            setLoading(true);
            const res = await getUserAnswersByAttempt({
                attempt_id: id,
                page: pageNo,
                limit,
            });

            if (res.data.success && res.data.data.length) {
                setAnswers(res.data.data);
                setAttemptInfo(res.data.data[0]);
                setPagination(res.data.pagination);
            }
        } catch (err) {
            console.error("Error fetching user answers:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id && !hasFetched.current) {
            hasFetched.current = true;
            fetchUserAnswers(page);
        }
    }, [id]);

    const handlePageChange = (pageNo) => {
        setPage(pageNo);
        fetchUserAnswers(pageNo);
    };

    return (
        <>
            <PageTitle
                activeMenu="View User Attempt / Answer"
                motherMenu="User Attempts"
                activeMenuLink={`view-user-attempt/${id}`}
                motherMenuLink="user-attempts"
            />

            {/* USER & ATTEMPT INFO */}
            <div className="card mb-3">
                <div className="card-body attempt-card">
                    {attemptInfo && (
                        <div
                            className={`attempt-stamp ${Number(attemptInfo.score_percentage) === 0
                                ? "danger"
                                : "success"
                                }`}
                        >
                            <div className="stamp-score">
                                {attemptInfo.correct_answers}/{attemptInfo.total_questions}
                            </div>
                            <div className="stamp-percentage">
                                {attemptInfo.score_percentage}%
                            </div>
                        </div>
                    )}

                    <h5 className="fw-bold mb-3">User Information</h5>

                    <p>
                        <strong>Name:</strong> {attemptInfo?.user_name || "-"}
                    </p>
                    <p>
                        <strong>Phone:</strong> {attemptInfo?.user_mobile || "-"}
                    </p>

                    {attemptInfo && (
                        <div className="mt-3">
                            <p>
                                <strong>Total Questions:</strong>{" "}
                                {attemptInfo.total_questions}
                            </p>
                            <p>
                                <strong>Correct Answers:</strong>{" "}
                                {attemptInfo.correct_answers}
                            </p>
                            <p>
                                <strong>Score:</strong>{" "}
                                {attemptInfo.score_percentage}%
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* QUESTIONS & ANSWERS */}
            <div className="card">
                <div className="card-body">
                    <h5 className="fw-bold mb-3">Questions & Answers</h5>

                    {loading ? (
                        <Spinner animation="border" />
                    ) : answers.length === 0 ? (
                        <p>No answers found</p>
                    ) : (
                        answers.map((item, index) => {
                            const isCorrect =
                                item.question_answer === item.user_answer;

                            return (
                                <div key={item.id} className="mb-4 border-bottom pb-3">
                                    <p className="fw-semibold d-flex justify-content-between align-items-center mb-1">
                                        <span>
                                            Q{(page - 1) * limit + index + 1}.{" "}
                                            {item.question_text || "—"}
                                        </span>

                                        <span
                                            className={
                                                isCorrect
                                                    ? "mark-positive"
                                                    : "mark-negative"
                                            }
                                        >
                                            {isCorrect ? "+1" : "-1"}
                                        </span>
                                    </p>

                                    {item.question_description && (
                                        <p className="text-muted small mb-2">
                                            {item.question_description}
                                        </p>
                                    )}

                                    <p>
                                        <strong>User Answer:</strong>{" "}
                                        <span
                                            className={
                                                isCorrect
                                                    ? "text-success"
                                                    : "text-danger"
                                            }
                                        >
                                            {item.user_answer || "—"}
                                        </span>

                                        <span
                                            className={`badge ms-2 ${isCorrect
                                                ? "bg-success"
                                                : "bg-danger"
                                                }`}
                                        >
                                            {isCorrect ? "Correct" : "Wrong"}
                                        </span>
                                    </p>

                                    {item.question_answer && (
                                        <p>
                                            <strong>Correct Answer:</strong>{" "}
                                            <span className="text-success">
                                                {item.question_answer}
                                            </span>
                                        </p>
                                    )}
                                </div>
                            );
                        })
                    )}

                    {/* PAGINATION */}
                    {pagination.totalPages > 1 && (
                        <Pagination className="mt-3">
                            {[...Array(pagination.totalPages)].map((_, i) => (
                                <Pagination.Item
                                    key={i + 1}
                                    active={page === i + 1}
                                    onClick={() => handlePageChange(i + 1)}
                                >
                                    {i + 1}
                                </Pagination.Item>
                            ))}
                        </Pagination>
                    )}
                </div>
            </div>
        </>
    );
};

export default ViewUserQuizAttempt;